/**
 * useGroupFirstPaging — the Camera Groups page's camera grid, with the SELECTED GROUP's
 * cameras listed first and every other camera straight after them.
 *
 * The grid used to page the node's camera list straight from the server, so a group's members
 * sat wherever the server's order put them. Selecting a group whose cameras were on pages 4
 * and 7 showed page 1 with nothing ticked, and the operator had to page through every camera
 * to find out what the group already held. A camera ticked on the last page then vanished from
 * view on the next page turn, with only a "+1" count to say it had been picked at all.
 *
 * Now the caller loads the WHOLE camera list (ids + card fields; it is the cards' live
 * players, not the list, that cost — and only one page of those is ever mounted) and this
 * pages it as one sequence:
 *
 *   [ the group: saved members, then cameras ticked this session ]  [ every other camera ]
 *
 * Paged 9 at a time with no gaps: the page where the group ends is topped up with the first
 * cameras that could be added to it, so a 3-camera group doesn't leave six empty slots on page
 * 1 and push every candidate onto page 2. `groupOnPage` says where the split falls, so the page
 * can draw a divider between "in the group" and "add to it".
 *
 * STABILITY. Which cameras count as "in the group run" is snapshotted when the page, group,
 * filters or search change — NOT on every tick. Ticking a camera on page 7 must not make its
 * card jump out from under the cursor to page 1, nor shift every card after it; it joins the
 * group run on the next page turn, which is exactly when the operator looks.
 *
 * Node-agnostic: the caller fetches (local REST, cloud tunnel); this only orders and slices.
 */
import { useMemo, useRef } from 'react';

type Cam = Record<string, any>;

export const cameraIdOf = (c: Cam): string => String(c.id ?? c.camera_id ?? '');

export interface UseGroupFirstPagingOptions {
  /** The page the operator is on (1-based). */
  page: number;
  pageSize: number;
  /** The selected group, or null — with no group the grid is the plain list. */
  groupId: string | null;
  /** Ids of the group's SAVED members. */
  memberIds: Set<string>;
  /** Cameras ticked for adding, not yet saved. */
  pendingAdd: Set<string>;
  /** Every camera the list returned for the current search, in the server's order. */
  cameras: Cam[];
  /** 'mapped' shows the group run only, 'unmapped' the other run only. */
  filterMapped?: 'all' | 'mapped' | 'unmapped' | string;
  /** Extra narrowing (tag, location) — applied BEFORE paging so pages stay full. */
  include?: (camera: Cam) => boolean;
  /** Changes whenever `include` would answer differently (it is usually an inline closure). */
  includeKey?: string;
}

export interface GroupFirstPaging {
  /** The cards for this page, in order. */
  pageCameras: Cam[];
  /** How many of `pageCameras` (from the front) belong to the group run. */
  groupOnPage: number;
  totalPages: number;
  /** Cameras in the group run right now: saved members plus ticked ones. */
  groupCount: number;
  /** Cameras after the group run. */
  otherCount: number;
  /** The page the group run ends on (0 when there is no group run). */
  groupLastPage: number;
}

export function useGroupFirstPaging({
  page,
  pageSize,
  groupId,
  memberIds,
  pendingAdd,
  cameras,
  filterMapped = 'all',
  include,
  includeKey = '',
}: UseGroupFirstPagingOptions): GroupFirstPaging {
  const includeRef = useRef(include);
  includeRef.current = include;

  const showGroupRun = !!groupId && filterMapped !== 'unmapped';
  const showOthers = !groupId || filterMapped !== 'mapped';

  /** The cameras that pass the tag / location filters, in server order. */
  const visible = useMemo(
    () => (includeRef.current ? cameras.filter((c) => includeRef.current!(c)) : cameras),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cameras, includeKey],
  );

  /** The live group run: saved members by name, then ticked additions in the order ticked. */
  const liveGroupIds = useMemo(() => {
    if (!showGroupRun) return [] as string[];
    const byId = new Map(visible.map((c) => [cameraIdOf(c), c]));
    const saved = visible
      .filter((c) => memberIds.has(cameraIdOf(c)))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
      .map(cameraIdOf);
    const seen = new Set(saved);
    const ticked = [...pendingAdd].filter((id) => !seen.has(id) && byId.has(id));
    return [...saved, ...ticked];
  }, [showGroupRun, visible, memberIds, pendingAdd]);

  // The snapshot (see STABILITY): frozen until the operator changes page, group, filter or
  // search — or saves / discards, which empties `pendingAdd` and is when the run should settle.
  const liveRef = useRef(liveGroupIds);
  liveRef.current = liveGroupIds;
  const pendingEmpty = pendingAdd.size === 0;
  const groupIds = useMemo(
    () => liveRef.current,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, groupId, filterMapped, visible, memberIds, pendingEmpty, showGroupRun],
  );

  const sequence = useMemo(() => {
    const byId = new Map(visible.map((c) => [cameraIdOf(c), c]));
    const head = groupIds.map((id) => byId.get(id)).filter(Boolean) as Cam[];
    if (!showOthers) return { list: head, groupCount: head.length };
    const inHead = new Set(groupIds);
    // 'unmapped' has no group run to hold the members, so they are dropped here instead.
    // Ticked-but-unsaved ones stay, marked "+ ADD", so a tick never makes a card vanish.
    const unmappedOnly = !!groupId && filterMapped === 'unmapped';
    const tail = visible.filter((c) => {
      const id = cameraIdOf(c);
      return !inHead.has(id) && !(unmappedOnly && memberIds.has(id));
    });
    return { list: [...head, ...tail], groupCount: head.length };
  }, [visible, groupIds, showOthers, groupId, filterMapped, memberIds]);

  const totalPages = Math.max(1, Math.ceil(sequence.list.length / pageSize));
  const start = (Math.min(page, totalPages) - 1) * pageSize;
  const pageCameras = sequence.list.slice(start, start + pageSize);
  const groupOnPage = Math.max(0, Math.min(pageCameras.length, sequence.groupCount - start));

  return {
    pageCameras,
    groupOnPage,
    totalPages,
    groupCount: liveGroupIds.length,
    otherCount: sequence.list.length - sequence.groupCount,
    groupLastPage: sequence.groupCount ? Math.ceil(sequence.groupCount / pageSize) : 0,
  };
}
